/**
 * Career Trust Layer rollout switches and demo-supply gating.
 *
 * Two independent mechanisms live here because they answer different questions:
 *
 *  - `trust_feature_flags` (server) decides whether a trust surface is live. Flags
 *    are read from the database so a rollback never requires a frontend deploy.
 *  - Demo supply (build-time) decides whether synthetic Weekly Drop companies and
 *    sample candidate cards may be produced at all. A trust product cannot mix
 *    synthetic and real supply, so this is deliberately impossible to enable in a
 *    production build.
 *
 * Reference: docs/STARTINUP_CAREER_TRUST_TECHNICAL_SPEC.md §13 Stage 0.
 */

import { supabase } from './supabaseClient';

export type TrustFeatureFlagKey =
  | 'career_proof_layer'
  | 'human_verification'
  | 'referral_passport'
  | 'talent_discovery'
  | 'weekly_drop_proof_import'
  | 'demo_supply';

// ─── Demo supply ──────────────────────────────────────────────

/**
 * Whether synthetic Weekly Drop supply may be generated.
 *
 * Requires an explicit opt-in **and** a non-production build. Both conditions are
 * required so that a stray environment variable in a production deployment cannot
 * put fabricated companies or candidates into a real queue or into metrics.
 */
export function isDemoSupplyEnabled(): boolean {
  const optedIn = process.env.REACT_APP_ENABLE_DEMO_SUPPLY === 'true';
  const nonProduction = process.env.NODE_ENV !== 'production';
  return optedIn && nonProduction;
}

/**
 * Marks a record as synthetic so downstream consumers, analytics, and metrics can
 * exclude it. Any object carrying this field must never be counted as real supply.
 */
export const DEMO_RECORD_MARKER = '__demo_synthetic' as const;

export interface DemoMarked {
  [DEMO_RECORD_MARKER]?: true;
}

export function markDemo<T extends object>(record: T): T & DemoMarked {
  return { ...record, [DEMO_RECORD_MARKER]: true as const };
}

export function isDemoRecord(record: unknown): boolean {
  return Boolean(record && typeof record === 'object' && (record as DemoMarked)[DEMO_RECORD_MARKER]);
}

/** Strips synthetic records from any collection before it reaches metrics. */
export function excludeDemoRecords<T>(records: T[]): T[] {
  return records.filter((record) => !isDemoRecord(record));
}

// ─── Server-controlled feature flags ──────────────────────────

const FLAG_CACHE_TTL_MS = 60_000;

let flagCache: Record<string, boolean> | null = null;
let flagCacheLoadedAt = 0;
let inFlight: Promise<Record<string, boolean>> | null = null;

/**
 * Loads all trust flags once per minute. A read failure resolves to "everything
 * off" rather than throwing, so an unreachable flag table hides new surfaces
 * instead of exposing them.
 */
export async function loadTrustFlags(forceRefresh = false): Promise<Record<string, boolean>> {
  const fresh = flagCache && Date.now() - flagCacheLoadedAt < FLAG_CACHE_TTL_MS;
  if (!forceRefresh && fresh) return flagCache!;
  if (inFlight) return inFlight;

  inFlight = (async () => {
    try {
      const { data, error } = await supabase
        .from('trust_feature_flags')
        .select('flag_key, enabled');
      if (error) throw error;
      const next: Record<string, boolean> = {};
      (data || []).forEach((row: { flag_key: string; enabled: boolean }) => {
        next[row.flag_key] = Boolean(row.enabled);
      });
      flagCache = next;
      flagCacheLoadedAt = Date.now();
      return next;
    } catch {
      // Fail closed. An unknown flag state is treated as disabled.
      flagCache = flagCache || {};
      flagCacheLoadedAt = Date.now();
      return flagCache;
    } finally {
      inFlight = null;
    }
  })();

  return inFlight;
}

export async function isTrustFeatureEnabled(key: TrustFeatureFlagKey): Promise<boolean> {
  const flags = await loadTrustFlags();
  return Boolean(flags[key]);
}

/** Clears the cache. Used when a session changes or after an admin flag update. */
export function resetTrustFlagCache(): void {
  flagCache = null;
  flagCacheLoadedAt = 0;
}
